import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaLinkedin, FaCheck, FaTimes, FaExternalLinkAlt, FaSpinner
} from 'react-icons/fa';
import { linkedinApi, isAuthenticated } from '../../services/api';
import { AdminLayout } from '../../components/admin';

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [linkedinStatus, setLinkedinStatus] = useState({ connected: false, loading: true });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const checkLinkedInStatus = useCallback(async () => {
    try {
      const response = await linkedinApi.getStatus();
      if (response.success) {
        setLinkedinStatus({ ...response.data, loading: false });
      } else {
        setLinkedinStatus({ connected: false, loading: false });
      }
    } catch (err) {
      console.error('Failed to check LinkedIn status:', err);
      setLinkedinStatus({ connected: false, loading: false });
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin');
      return;
    }

    // Check for LinkedIn callback status
    const linkedinParam = searchParams.get('linkedin');
    if (linkedinParam === 'success') {
      setMessage({ type: 'success', text: 'LinkedIn connected successfully!' });
      // Clean URL
      window.history.replaceState({}, document.title, '/admin/settings');
    } else if (linkedinParam === 'error') {
      const errorMessage = searchParams.get('message') || 'Failed to connect LinkedIn';
      setMessage({ type: 'error', text: decodeURIComponent(errorMessage) });
      window.history.replaceState({}, document.title, '/admin/settings');
    }

    checkLinkedInStatus();
  }, [navigate, searchParams, checkLinkedInStatus]);

  const handleConnectLinkedIn = async () => {
    setConnecting(true);
    try {
      const response = await linkedinApi.getAuthUrl();
      if (response.success && response.data?.authUrl) {
        // Redirect to LinkedIn OAuth
        window.location.href = response.data.authUrl;
      } else {
        setMessage({ type: 'error', text: 'Failed to get LinkedIn authorization URL' });
        setConnecting(false);
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to connect to LinkedIn' });
      setConnecting(false);
    }
  };

  const handleDisconnectLinkedIn = async () => {
    if (!window.confirm('Are you sure you want to disconnect your LinkedIn account?')) {
      return;
    }

    setDisconnecting(true);
    try {
      const response = await linkedinApi.disconnect();
      if (response.success) {
        setLinkedinStatus({ connected: false, loading: false });
        setMessage({ type: 'success', text: 'LinkedIn disconnected successfully' });
      } else {
        setMessage({ type: 'error', text: response.message || 'Failed to disconnect LinkedIn' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to disconnect LinkedIn' });
    } finally {
      setDisconnecting(false);
    }
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-dark-800">Settings</h1>
          <p className="text-dark-500 mt-1">Manage your integrations and preferences</p>
        </div>

        {/* Message */}
        {message.text && (
          <motion.div
            className={`px-4 py-3 rounded-lg flex items-center justify-between ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center space-x-2">
              {message.type === 'success' ? <FaCheck /> : <FaTimes />}
              <span>{message.text}</span>
            </div>
            <button onClick={() => setMessage({ type: '', text: '' })} className="text-lg">&times;</button>
          </motion.div>
        )}

        {/* LinkedIn Integration */}
        <motion.div
          className="bg-white rounded-xl shadow-sm overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-dark-800">LinkedIn Integration</h2>
            <p className="text-sm text-dark-500 mt-1">
              Connect your LinkedIn account to automatically share blog posts
            </p>
          </div>

          <div className="p-6">
            {linkedinStatus.loading ? (
              <div className="flex items-center justify-center py-8">
                <FaSpinner className="animate-spin w-6 h-6 text-primary-500" />
              </div>
            ) : linkedinStatus.connected ? (
              /* Connected State */
              <div className="space-y-4">
                <div className="flex items-center space-x-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <FaLinkedin className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-dark-800">LinkedIn Connected</span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                    </div>
                    {linkedinStatus.expiresAt && (
                      <p className="text-sm text-dark-500 mt-1">
                        Token expires: {formatExpiryDate(linkedinStatus.expiresAt)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-dark-700">Auto-Post to LinkedIn</p>
                    <p className="text-sm text-dark-500">
                      When enabled on a blog post, it will automatically share to LinkedIn when published
                    </p>
                  </div>
                  <FaCheck className="w-5 h-5 text-green-500" />
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <button
                    onClick={handleDisconnectLinkedIn}
                    disabled={disconnecting}
                    className="flex items-center space-x-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    {disconnecting ? (
                      <FaSpinner className="animate-spin w-4 h-4" />
                    ) : (
                      <FaTimes className="w-4 h-4" />
                    )}
                    <span>Disconnect LinkedIn</span>
                  </button>
                </div>
              </div>
            ) : (
              /* Not Connected State */
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLinkedin className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-dark-800 mb-2">
                  Connect Your LinkedIn Account
                </h3>
                <p className="text-dark-500 mb-6 max-w-md mx-auto">
                  Connect your LinkedIn account to automatically share your blog posts with your professional network.
                </p>

                <button
                  onClick={handleConnectLinkedIn}
                  disabled={connecting}
                  className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {connecting ? (
                    <>
                      <FaSpinner className="animate-spin w-5 h-5" />
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <>
                      <FaLinkedin className="w-5 h-5" />
                      <span>Connect LinkedIn</span>
                      <FaExternalLinkAlt className="w-3 h-3" />
                    </>
                  )}
                </button>

                <p className="text-xs text-dark-400 mt-4">
                  You will be redirected to LinkedIn to authorize the connection
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div
          className="bg-white rounded-xl shadow-sm overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-dark-800">How It Works</h2>
          </div>
          <div className="p-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 font-bold text-sm">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-dark-800">Connect Account</h4>
                  <p className="text-sm text-dark-500">
                    Authorize the app to post on your behalf
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 font-bold text-sm">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-dark-800">Write Blog</h4>
                  <p className="text-sm text-dark-500">
                    Create your blog with auto-post toggle enabled
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 font-bold text-sm">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-dark-800">Auto Share</h4>
                  <p className="text-sm text-dark-500">
                    AI generates and posts to LinkedIn automatically
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default Settings;
